import React, { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import { UseFormReturn } from "react-hook-form";
import { Label } from "@/components/ui/label";
import { MapPin } from "lucide-react";
import { CreateStoreFormData } from "./createStoreSchema";
import { useToast } from "@/hooks/use-toast";
import { debounce } from "@/utils/debounce";
import "mapbox-gl/dist/mapbox-gl.css";
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";

interface MapboxAddressInputProps {
  form?: UseFormReturn<CreateStoreFormData>;
  onLocationSelect?: (latitude: number, longitude: number) => void;
  initialLocation?: {
    latitude: number;
    longitude: number;
  };
  showForm?: boolean;
}

export const MapboxAddressInput: React.FC<MapboxAddressInputProps> = ({
  form,
  onLocationSelect,
  initialLocation,
  showForm,
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const geocoder = useRef<MapboxGeocoder | null>(null);
  const marker = useRef<mapboxgl.Marker | null>(null);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [userCountry, setUserCountry] = useState<string>("");
  const { toast } = useToast();

  // Mapbox access token
  const MAPBOX_TOKEN =
    "pk.eyJ1IjoiZW1teWR1cnVjIiwiYSI6ImNtYjNtNnNoMDA4cGMyaXNhb3g1Zm14cTIifQ.rYjpHB0JB7V4VO4amNJs-g";

  // Get user's country code based on their location
  const getUserCountry = async (): Promise<string> => {
    try {
      const response = await fetch("https://ipapi.co/country/");
      const country = await response.text();
      return country.toLowerCase();
    } catch (error) {
      console.log("Could not detect user country, defaulting to worldwide");
      return "";
    }
  };

  // Debounced update function
  const debouncedUpdateMap = useRef(
    debounce((result: any) => {
      updateFormFromGeocoder(result);
    }, 300)
  ).current;

  useEffect(() => {
    const initializeMap = async () => {
      if (!mapContainer.current) return;

      // Get user's country and initialize location
      const country = await getUserCountry();
      setUserCountry(country);

      // Get initial location
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const { latitude, longitude } = position.coords;

            // Get initial address data
            const response = await fetch(
              `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?access_token=${MAPBOX_TOKEN}&types=address`
            );
            const data = await response.json();

            if (data.features && data.features.length > 0) {
              updateFormFromGeocoder(data.features[0]);
            }

            // Initialize map with user's location
            initializeMapWithLocation(latitude, longitude, country);
          },
          () => {
            // Fallback to country center if location access denied
            initializeMapWithLocation(0, 20, country);
          }
        );
      } else {
        initializeMapWithLocation(0, 20, country);
      }
    };

    initializeMap();

    return () => {
      if (map.current) map.current.remove();
    };
  }, []);

  const initializeMapWithLocation = (
    lat: number,
    lng: number,
    country: string
  ) => {
    mapboxgl.accessToken = MAPBOX_TOKEN;

    map.current = new mapboxgl.Map({
      container: mapContainer.current!,
      style: "mapbox://styles/mapbox/streets-v11",
      center: [lng, lat],
      zoom: country ? 6 : 2,
    });

    // Initialize geocoder with country restriction
    geocoder.current = new MapboxGeocoder({
      accessToken: MAPBOX_TOKEN,
      mapboxgl: mapboxgl as any,
      marker: false,
      placeholder: "Search for address...",
    });

    // Fix: Cast to any to avoid type compatibility issues
    map.current.addControl(geocoder.current as any, "top-left");

    // Handle geocoder result with debounce
    geocoder.current.on("result", (e) => {
      debouncedUpdateMap(e.result);
      updateMapMarker(e.result.center);
    });
  };

  const updateMapMarker = (coordinates: [number, number]) => {
    if (marker.current) {
      marker.current.remove();
    }
    marker.current = new mapboxgl.Marker()
      .setLngLat(coordinates)
      .addTo(map.current!);
  };

  const updateFormFromGeocoder = (result: any) => {
    const { place_name, context, text } = result;

    // Extract address components
    let street = text || "";
    let streetNumber = "";
    let city = "";
    let state = "";
    let zip = "";

    // Parse context for detailed address info
    if (context) {
      context.forEach((item: any) => {
        if (item.id.includes("postcode")) {
          zip = item.text;
        } else if (item.id.includes("place")) {
          city = item.text;
        } else if (item.id.includes("region")) {
          state = item.text;
        } else if (item.id.includes("address")) {
          const addressParts = item.text.split(" ");
          if (addressParts.length > 1) {
            streetNumber = addressParts[addressParts.length - 1];
            street = addressParts.slice(0, -1).join(" ");
          }
        }
      });
    }

    // If state is empty, use city as fallback
    if (!state && city) {
      state = city;
    }

    // If no street number found, try to extract from place_name
    if (!streetNumber && place_name) {
      const match = place_name.match(/(\d+)/);
      if (match) {
        streetNumber = match[1];
        street = street.replace(streetNumber, "").trim();
      }
    }

    // Update form values
    form?.setValue("street", street);
    form?.setValue("streetNumber", streetNumber);
    form?.setValue("city", city);
    form?.setValue("zip", zip);
    form?.setValue("state", state);

    toast({
      title: "Address updated",
      description:
        "Address fields have been filled automatically. You can modify them if needed.",
    });
  };

  const detectCurrentLocation = () => {
    setIsDetectingLocation(true);

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;

          // Reverse geocoding to get address without country restriction
          fetch(
            `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?access_token=${MAPBOX_TOKEN}&types=address`
          )
            .then((response) => response.json())
            .then((data) => {
              if (data.features && data.features.length > 0) {
                const result = data.features[0];
                updateFormFromGeocoder(result);

                // Update map view
                if (map.current) {
                  map.current.flyTo({
                    center: [longitude, latitude],
                    zoom: 16,
                  });

                  // Add or update marker
                  if (marker.current) {
                    marker.current.remove();
                  }
                  marker.current = new mapboxgl.Marker()
                    .setLngLat([longitude, latitude])
                    .addTo(map.current);
                }
              }
            })
            .catch((error) => {
              console.error("Reverse geocoding error:", error);
              toast({
                title: "Location detection failed",
                description:
                  "Unable to detect your current location. Please search manually.",
                variant: "destructive",
              });
            })
            .finally(() => {
              setIsDetectingLocation(false);
            });
        },
        (error) => {
          console.error("Geolocation error:", error);
          toast({
            title: "Location access denied",
            description: "Please allow location access or search manually.",
            variant: "destructive",
          });
          setIsDetectingLocation(false);
        }
      );
    } else {
      toast({
        title: "Geolocation not supported",
        description: "Your browser doesn't support location detection.",
        variant: "destructive",
      });
      setIsDetectingLocation(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <MapPin className="h-5 w-5" />
        <Label className="text-lg font-medium">Store Location</Label>
      </div>

      <p className="text-sm text-muted-foreground">
        Search for your store address worldwide or use current location. You can
        also manually enter the details below.
      </p>

      <div
        ref={mapContainer}
        className="w-full h-64 rounded-lg border"
        style={{ minHeight: "256px" }}
      />
    </div>
  );
};
